import React, { useState } from 'react';
import { TextField, Button, Typography, Box, IconButton, Divider } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { GoogleLogin } from '@react-oauth/google';
import api from '../api';
import GlassCard from './GlassCard';
import { PRIMARY, ERROR_LIGHT, PRIMARY_GRADIENT, PRIMARY_GRADIENT_HOVER } from '../colors';

function Login({ onLogin, onSwitchToRegister, onForgotPassword, onResendVerification, onGoogleLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!username.trim()) newErrors.username = 'Username is required';
    if (!password) newErrors.password = 'Password is required';
    if (username.length < 3) newErrors.username = 'Username must be at least 3 characters';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    setNeedsVerification(false);
    try {
      // Convert username to lowercase for case-insensitive login
      await onLogin(username.toLowerCase().trim(), password);
    } catch (error) {
      // Check if error is due to email verification
      if (error.response?.data?.email_verification_required) {
        setNeedsVerification(true);
        setErrors({ 
          general: error.response.data.error || 'Please verify your email address before logging in.'
        });
      } else {
        const errorMsg = error.response?.data?.error || 
                        error.response?.data?.detail || 
                        'Login failed. Please check your credentials.';
        setErrors({ general: errorMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign-In success
  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setErrors({});
    try {
      const response = await api.post('/auth/google/', {
        credential: credentialResponse.credential
      });
      
      // Store tokens
      localStorage.setItem('token', response.data.access);
      localStorage.setItem('refresh', response.data.refresh);
      
      // Call the parent's onGoogleLogin handler
      if (onGoogleLogin) {
        onGoogleLogin(response.data);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.error || 
                      'Google sign-in failed. Please try again.';
      setErrors({ general: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Handle Google Sign-In error
  const handleGoogleError = () => {
    setErrors({ general: 'Google sign-in was cancelled or failed.' });
  };

  // Common input styles for dark theme
  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: 2,
      color: '#fff',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: PRIMARY,
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.6)',
      '&.Mui-focused': {
        color: PRIMARY,
      },
    },
    '& .MuiFormHelperText-root': {
      color: ERROR_LIGHT,
    },
  };

  return (
    <Box 
      component="form" 
      onSubmit={handleSubmit} 
      sx={{ 
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #0a0a0f 0%, #12121a 100%)',
        px: { xs: 2, sm: 0 },
        py: 4,
      }}
    >
      <GlassCard 
        hover={false}
        sx={{ 
          p: { xs: 3, sm: 4, md: 5 },
          maxWidth: 440,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: PRIMARY,
          },
        }}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              background: PRIMARY_GRADIENT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              boxShadow: '0 12px 32px rgba(59, 130, 246, 0.4)',
            }}
          >
            <img 
              src={`${process.env.PUBLIC_URL || ''}/logo.jpg`}
              alt="FleetPulse"
              style={{ 
                width: 56, 
                height: 56, 
                borderRadius: 8,
                objectFit: 'cover',
              }}
            />
          </Box>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              background: PRIMARY_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 1,
            }}
          >
            FleetPulse
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
            Welcome back! Sign in to continue.
          </Typography>
        </Box>
        
        {errors.general && (
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            borderRadius: 2, 
            bgcolor: `rgba(239, 68, 68, 0.1)`,
            border: `1px solid rgba(239, 68, 68, 0.3)`,
          }}>
            <Typography sx={{ color: ERROR_LIGHT, fontSize: '0.9rem' }}>{errors.general}</Typography>
            {needsVerification && (
              <Button 
                onClick={onResendVerification} 
                variant="outlined" 
                size="small"
                fullWidth
                sx={{ 
                  mt: 2,
                  borderColor: PRIMARY,
                  color: PRIMARY,
                  '&:hover': {
                    borderColor: '#5a6fd6',
                    bgcolor: 'rgba(59, 130, 246, 0.1)',
                  },
                }}
              >
                Resend Verification Email
              </Button>
            )}
          </Box>
        )}
        
        <TextField
          label="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          fullWidth
          margin="normal"
          required
          error={!!errors.username}
          helperText={errors.username}
          sx={inputStyles}
        />
        <TextField
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
          required
          error={!!errors.password}
          helperText={errors.password}
          sx={inputStyles}
          InputProps={{
            endAdornment: (
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            ),
          }}
        />
        
        <Button 
          type="submit" 
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{ 
            mt: 3,
            py: 1.5,
            background: PRIMARY_GRADIENT,
            fontWeight: 600,
            fontSize: '1rem',
            borderRadius: 2,
            textTransform: 'none',
            boxShadow: '0 8px 24px rgba(59, 130, 246, 0.4)',
            '&:hover': {
              background: PRIMARY_GRADIENT_HOVER,
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 32px rgba(59, 130, 246, 0.5)',
            },
            '&:disabled': {
              background: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.3)',
            },
          }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </Button>

        {/* Divider with "or" text */}
        <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
          <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
          <Typography sx={{ px: 2, color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.875rem' }}>
            or
          </Typography>
          <Divider sx={{ flex: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        </Box>

        {/* Google Sign-In Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            theme="filled_black"
            size="large"
            width={400}
            text="signin_with"
            shape="rectangular"
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
          <Button 
            onClick={onForgotPassword} 
            sx={{ 
              textTransform: 'none',
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                color: PRIMARY,
                bgcolor: 'transparent',
              },
            }} 
            size="small"
          >
            Forgot Password?
          </Button>
          <Button 
            onClick={onSwitchToRegister} 
            variant="outlined"
            sx={{
              borderColor: 'rgba(255, 255, 255, 0.2)',
              color: '#fff',
              textTransform: 'none',
              '&:hover': {
                borderColor: PRIMARY,
                bgcolor: 'rgba(59, 130, 246, 0.1)',
              },
            }}
          >
            Create Account
          </Button>
        </Box>
      </GlassCard>
    </Box>
  );
}

export default Login;
